import React, { useMemo, useState } from "react";
import { Alert, Button, Group, Loader, Modal, Tabs, Text } from "@mantine/core";
import { notifyError, notifySuccess } from "../../utils/notify";
import {
  IconAlertCircle,
  IconCopy,
  IconPencil,
  IconPlayerPlay,
  IconPlus,
} from "@tabler/icons-react";
import { FormCategory, FormTemplate } from "../../types/formTemplates";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { StandardTemplatesGrid } from "./StandardTemplatesGrid";
import { CustomTemplatesGrid } from "./CustomTemplatesGrid";
import {
  TemplateSortControl,
  usePersistedTemplateSort,
} from "./TemplateSortControl";
import { useSortedTemplates } from "./useSortedTemplates";
import primaryAction from "../common/primaryFilledButton.module.css";
import styles from "./TemplateLibraryModal.module.css";

type LibraryTab = "standard" | "custom";

interface TemplateLibraryModalProps {
  opened: boolean;
  onClose: () => void;
  onUseTemplate: (template: FormTemplate) => void;
  onEditCustom: (template: FormTemplate) => void;
  onCreateNew: () => void;
  /** Gate Create / Copy to Custom / Edit Template (template-level write actions) */
  canManageTemplates: boolean;
  /** Gate Use Template (assignment to client) */
  canManageClientForms: boolean;
  isAssigning?: boolean;
  /** Optional: only show templates of this category. When set, the modal
   * title swaps to the matching label (e.g. "Add SOAP Note" for
   * `per_visit_clinical`) and the "Create New Template" button is hidden
   * — scratch-build for category-filtered modals isn't wired here; callers
   * that want pre-seeded creation go through the settings page where
   * FormBuilder accepts a `defaultCategory` prop. */
  categoryFilter?: FormCategory;
  /** Optional override for the modal title. Defaults to "Form Templates"
   * or a category-specific label when categoryFilter is set. */
  title?: string;
}

export const TemplateLibraryModal: React.FC<TemplateLibraryModalProps> = ({
  opened,
  onClose,
  onUseTemplate,
  onEditCustom,
  onCreateNew,
  canManageTemplates,
  canManageClientForms,
  isAssigning = false,
  categoryFilter,
  title,
}) => {
  const {
    standardTemplates,
    customTemplates,
    isLoading,
    error,
    cloneTemplate,
  } = useFormTemplates();

  const [activeTab, setActiveTab] = useState<LibraryTab>("custom");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [sortBy, setSortBy] = usePersistedTemplateSort();

  const filteredStandard = useMemo(
    () =>
      categoryFilter
        ? standardTemplates.filter((t) => t.category === categoryFilter)
        : standardTemplates,
    [standardTemplates, categoryFilter],
  );
  const filteredCustom = useMemo(
    () =>
      categoryFilter
        ? customTemplates.filter((t) => t.category === categoryFilter)
        : customTemplates,
    [customTemplates, categoryFilter],
  );
  const sortedStandard = useSortedTemplates(filteredStandard, sortBy);
  const sortedCustom = useSortedTemplates(filteredCustom, sortBy);

  const modalTitle =
    title ??
    (categoryFilter === "per_visit_clinical"
      ? "Add SOAP Note"
      : categoryFilter === "per_visit"
        ? "Add Per-Visit Form"
        : categoryFilter === "reusable"
          ? "Add Reusable Form"
          : "Form Templates");

  const selected: FormTemplate | null = useMemo(() => {
    if (!selectedId) return null;
    return (
      filteredStandard.find((t) => t.id === selectedId) ||
      filteredCustom.find((t) => t.id === selectedId) ||
      null
    );
  }, [selectedId, filteredStandard, filteredCustom]);

  const isMock = Boolean(selected?.is_mock_placeholder);
  const isCustomSelected = selected?.source === "custom";
  const canUse = selected && !isMock && canManageClientForms;
  const canClone =
    canManageTemplates &&
    activeTab === "standard" &&
    Boolean(selected) &&
    !isMock;

  function handleTemplateSelect(template: FormTemplate) {
    setSelectedId((current) => (current === template.id ? null : template.id));
  }

  function handleTabChange(next: string | null) {
    if (next === "standard" || next === "custom") {
      setActiveTab(next);
      setSelectedId(null);
    }
  }

  function handleClose() {
    setSelectedId(null);
    onClose();
  }

  const handleCopyToCustom = async () => {
    if (!selected || activeTab !== "standard") return;
    const sourceSlug = selected.slug || selected.id;
    setIsCloning(true);
    try {
      const created = await cloneTemplate({
        clone_from_slug: sourceSlug,
        name: `${selected.name} (Copy)`,
        category: selected.category,
      });
      setActiveTab("custom");
      setSelectedId(created.id);
      notifySuccess(`${created.name} is ready to edit.`, {
        title: "Copied to your custom templates",
      });
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unexpected error.", {
        title: "Could not copy template",
      });
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={700} size="xl">
          {modalTitle}
        </Text>
      }
      size="64rem"
      centered
      radius="lg"
      padding={0}
      classNames={{
        content: styles.modalContent,
        body: styles.modalBody,
        header: styles.header,
      }}
      styles={{
        content: {
          width: "min(64rem, calc(100vw - 24px))",
        },
        body: {
          padding: 0,
          height: "min(560px, 78vh)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        classNames={{
          root: styles.tabs,
          list: styles.tabsList,
          tab: styles.tab,
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="custom">Custom Templates</Tabs.Tab>
          <Tabs.Tab value="standard">Standard Templates</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <div className={styles.contentArea}>
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Group justify="center" p="lg">
            <Loader size="sm" />
          </Group>
        )}

        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <TemplateSortControl value={sortBy} onChange={setSortBy} size="xs" />
          {activeTab === "custom" && canManageTemplates && !categoryFilter && (
            <Button
              variant="filled"
              className={primaryAction.primaryFilled}
              leftSection={<IconPlus size={16} stroke={1.75} />}
              onClick={onCreateNew}
            >
              Create New Template
            </Button>
          )}
        </Group>

        <div className={styles.scrollArea}>
          {activeTab === "standard" ? (
            <StandardTemplatesGrid
              templates={sortedStandard}
              selectedId={selectedId}
              onSelect={handleTemplateSelect}
            />
          ) : (
            <CustomTemplatesGrid
              templates={sortedCustom}
              selectedId={selectedId}
              onSelect={handleTemplateSelect}
            />
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <Text className={styles.footerHint}>
          {selected ? (
            <>
              Selected:{" "}
              <Text span fw={600}>
                {selected.name}
              </Text>
            </>
          ) : (
            "Click a template to select it"
          )}
        </Text>
        <div className={styles.footerActions}>
          <Button variant="subtle" color="gray" onClick={handleClose}>
            Cancel
          </Button>
          {canClone && (
            <Button
              variant="outline"
              className={primaryAction.primaryOutline}
              leftSection={<IconCopy size={16} />}
              onClick={handleCopyToCustom}
              loading={isCloning}
            >
              Copy to Custom
            </Button>
          )}
          {isCustomSelected && canManageTemplates && (
            <Button
              variant="outline"
              className={primaryAction.primaryOutline}
              leftSection={<IconPencil size={16} />}
              onClick={() => selected && onEditCustom(selected)}
            >
              Edit Template
            </Button>
          )}
          {canManageClientForms && (
            <Button
              variant="filled"
              className={primaryAction.primaryFilled}
              leftSection={<IconPlayerPlay size={16} />}
              disabled={!canUse}
              loading={isAssigning}
              onClick={() => selected && onUseTemplate(selected)}
            >
              Use Template
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
